<template>
  <div class="editor">

    <!-- Üst çubuk -->
    <header class="ed-top">
      <div class="ed-title">
        <h2>{{ template ? template.name : $t('common.loading') }}</h2>
        <span class="pill" :class="statusPill" v-if="template">{{ statusLabel }}</span>
      </div>

      <div class="ed-zoom">
        <button class="zoom-btn" :disabled="zoom <= 0.6" @click="setZoom(zoom - 0.2)">&minus;</button>
        <span class="zoom-value">{{ Math.round(zoom * 100) }}%</span>
        <button class="zoom-btn" :disabled="zoom >= 1.6" @click="setZoom(zoom + 0.2)">+</button>
        <button class="zoom-btn is-text" @click="setZoom(1)">{{ $t('editor.fitWidth') }}</button>
      </div>

      <div class="ed-actions">
        <button class="btn-ghost" :class="{ 'is-loading': saving }" @click="save(false)">
          {{ $t('common.save') }}
        </button>
        <button class="btn-primary" v-if="!isActive"
                :class="{ 'is-loading': saving }"
                :disabled="!hasSignature" @click="save(true)">
          <span class="icon"><i class="fa fa-check"></i></span>
          <span>{{ $t('editor.saveActivate') }}</span>
        </button>
        <button class="btn-ghost" v-else :class="{ 'is-loading': saving }" @click="save('draft')">
          {{ $t('editor.toDraft') }}
        </button>
        <button class="close-btn" @click="$emit('close')" aria-label="close">&times;</button>
      </div>
    </header>

    <!-- Uyarı: üstte, her zaman görünür -->
    <div class="toast is-err" v-if="errorMessage">
      <span class="toast-icon"><i class="fa fa-exclamation-triangle"></i></span>
      <span>{{ errorMessage }}</span>
      <button class="toast-close" @click="errorMessage = ''">&times;</button>
    </div>

    <div class="ed-body">

      <!-- Sol panel -->
      <aside class="ed-side">

        <!-- Hazır mı: en üstte, renkli -->
        <div class="ready" :class="hasSignature ? 'is-ok' : 'is-warn'">
          <span class="ready-icon">
            <i class="fa" :class="hasSignature ? 'fa-check-circle' : 'fa-exclamation-circle'"></i>
          </span>
          <span v-if="hasSignature">{{ $t('editor.ready', { count: signatureCount }) }}</span>
          <span v-else>{{ $t('editor.needSignature') }}</span>
        </div>

        <section class="panel">
          <h3>{{ $t('editor.addField') }}</h3>
          <p class="hint">{{ $t('editor.addHelp') }}</p>

          <!-- Alan türleri ızgara: açılır listede hangi türler var görünmüyordu -->
          <div class="types">
            <button v-for="type in fieldTypes" :key="type.value"
                    class="type" :class="{ 'is-active': newType === type.value }"
                    @click="newType = type.value">
              <i class="fa" :class="type.icon"></i>
              <span>{{ $t('editor.' + type.labelKey) }}</span>
            </button>
          </div>

          <label class="side-label">{{ $t('editor.party') }}</label>
          <div class="select-wrap">
            <select class="select-input" v-model="newRole" :disabled="!roles.length">
              <option v-for="r in roles" :value="r.key" :key="r.key">{{ r.name }}</option>
            </select>
          </div>
          <p class="err" v-if="!roles.length">{{ $t('editor.noRoles') }}</p>
        </section>

        <section class="panel">
          <div class="panel-head">
            <h3>{{ $t('editor.fields') }}</h3>
            <span class="count">{{ fields.length }}</span>
          </div>

          <p class="hint" v-if="!fields.length">{{ $t('editor.noFields') }}</p>

          <ul class="field-list" v-else>
            <li v-for="(f, i) in fields" :key="f.key"
                class="field-item" :class="{ 'is-selected': selectedKey === f.key }"
                @click="selectField(f)">
              <span class="dot" :class="isSignature(f) ? 'is-sign' : 'is-data'"></span>

              <div class="field-item-main">
                <input class="mini-input" v-model.trim="f.label"
                       :placeholder="$t('editor.label')" @click.stop>
                <div class="field-meta">
                  <span>{{ typeLabel(f.type) }}</span>
                  <span>·</span>
                  <span>{{ roleName(f.roleKey) }}</span>
                  <span>·</span>
                  <span>{{ $t('editor.page') }} {{ f.page }}</span>
                </div>
                <label class="mini-check" @click.stop>
                  <input type="checkbox" v-model="f.required">
                  <span>{{ $t('editor.requiredField') }}</span>
                </label>
              </div>

              <button class="icon-btn is-danger" :title="$t('editor.deleteField')"
                      @click.stop="removeField(i)">
                <i class="fa fa-times"></i>
              </button>
            </li>
          </ul>

          <p class="hint mt" v-if="fields.length">{{ $t('editor.selectHint') }}</p>
        </section>
      </aside>

      <!-- Belge alanı -->
      <main class="ed-main">
        <div class="stage" :style="{ maxWidth: (860 * zoom) + 'px' }">

          <div class="loading" v-if="docLoading">
            <i class="fa fa-circle-o-notch fa-spin"></i>
            <span>{{ $t('editor.preparing') }}</span>
          </div>

          <div v-for="(page, index) in pages" :key="index" class="page-block">
            <p class="page-no">{{ $t('editor.pageOf', { page: index + 1, total: pages.length }) }}</p>

            <div class="page-wrap" :ref="'page' + index"
                 :style="{ paddingTop: (page.ratio * 100) + '%' }"
                 @click="placeField($event, index)">

              <canvas v-if="source === 'upload'" :ref="'canvas' + index" class="page-canvas"></canvas>
              <div v-else class="page-canvas page-html content" v-html="html"></div>

              <div v-for="f in fieldsOnPage(index + 1)" :key="f.key"
                   class="field-box"
                   :class="{ 'is-signature': isSignature(f), 'is-selected': selectedKey === f.key }"
                   :style="boxStyle(f)"
                   @mousedown.stop="startDrag($event, f, index)"
                   @click.stop="selectField(f)">
                <span class="field-tag">
                  <i class="fa" :class="typeIcon(f.type)"></i>
                  {{ f.label }}
                </span>
                <button class="box-del" :title="$t('editor.deleteField')"
                        @mousedown.stop @click.stop="removeByKey(f.key)">&times;</button>
                <span class="resize-handle" @mousedown.stop="startResize($event, f, index)"></span>
              </div>
            </div>
          </div>

          <div class="empty-doc" v-if="!docLoading && !pages.length">
            {{ $t('editor.cannotShow') }}
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import api from '../config/api';

// pdf.js'i CDN'den bir kez yüklüyoruz. npm paketi webpack worker ayarı
// istiyor; bu yol Vue 2 + eski webpack kurulumunda daha az sürprizli.
const PDFJS_URL = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.min.js';
const PDFJS_WORKER = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

let pdfjsPromise = null;
function loadPdfJs() {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = PDFJS_URL;
    script.onload = () => {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error('pdf.js could not be loaded'));
    document.head.appendChild(script);
  });
  return pdfjsPromise;
}

const DEFAULT_SIZE = {
  signature: { w: 0.26, h: 0.075 },
  initials: { w: 0.10, h: 0.055 },
  checkbox: { w: 0.04, h: 0.025 },
  default: { w: 0.24, h: 0.032 }
};

export default {
  name: 'TemplateEditor',
  props: {
    templateId: { type: String, required: true }
  },
  data() {
    return {
      // Kullanımdaki şablon kaydedilince backend yeni versiyon açıyor ve yeni
      // bir _id dönüyor. Prop'taki id'de kalırsak ikinci kaydetme yine eski
      // versiyonu düzenler ve her seferinde yeni bir dal açılır.
      currentId: this.templateId,
      template: null,
      fields: [],
      roles: [],
      source: 'blank',
      html: '',

      pages: [],
      docLoading: true,
      pdf: null,

      newType: 'signature',
      newRole: '',
      selectedKey: '',
      zoom: 1,

      // Tür listesi veriden: açılır listede hangi türlerin olduğu
      // görünmüyordu, ızgarada hepsi aynı anda duruyor
      fieldTypes: [
        { value: 'signature', icon: 'fa-pencil', labelKey: 'tSignature' },
        { value: 'initials', icon: 'fa-font', labelKey: 'tInitials' },
        { value: 'text', icon: 'fa-align-left', labelKey: 'tText' },
        { value: 'date', icon: 'fa-calendar', labelKey: 'tDate' },
        { value: 'number', icon: 'fa-hashtag', labelKey: 'tNumber' },
        { value: 'email', icon: 'fa-envelope-o', labelKey: 'tEmail' },
        { value: 'checkbox', icon: 'fa-check-square-o', labelKey: 'tCheckbox' }
      ],

      saving: false,
      errorMessage: '',

      drag: null
    };
  },
  computed: {
    hasSignature() {
      return this.signatureCount > 0;
    },
    isActive() {
      return !!this.template && this.template.status === 'active';
    },
    signatureCount() {
      return this.fields.filter((f) => f.type === 'signature' || f.type === 'initials').length;
    },
    statusLabel() {
      const s = this.template ? this.template.status : '';
      const map = { draft: 'statusDraft', active: 'statusActive', archived: 'statusArchived' };
      return map[s] ? this.$t('templates.' + map[s]) : s;
    },
    statusPill() {
      const s = this.template ? this.template.status : '';
      return { draft: 'is-warn', active: 'is-ok', archived: 'is-idle' }[s] || 'is-idle';
    }
  },
  mounted() {
    this.loadTemplate();
    // Sürükleme belge dışına taşabilir, dinleyiciler document üzerinde
    document.addEventListener('mousemove', this.onDrag);
    document.addEventListener('mouseup', this.endDrag);
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.endDrag);
  },
  methods: {
    loadTemplate() {
      api.get('/templates/' + this.currentId)
        .then((body) => {
          const t = body.template;
          this.template = t;
          this.fields = (t.fields || []).map((f) => Object.assign({}, f));
          this.roles = t.roles || [];
          this.source = t.source;
          this.html = t.content ? t.content.html : '';
          this.newRole = this.roles.length ? this.roles[0].key : '';

          if (t.source === 'upload') this.loadPdf();
          else this.setupHtmlPage();
        })
        .catch((err) => {
          this.errorMessage = err.message;
          this.docLoading = false;
        });
    },

    // Sıfırdan yazılan şablon A4 oranında tek sayfa olarak gösteriliyor;
    // üretilen PDF de A4 olduğu için koordinatlar birebir eşleşiyor
    setupHtmlPage() {
      this.pages = [{ ratio: 841.89 / 595.28 }];
      this.docLoading = false;
    },

    loadPdf() {
      Promise.all([
        loadPdfJs(),
        api.get('/templates/' + this.currentId + '/file', { responseType: 'blob' })
      ])
        .then(([pdfjsLib, blob]) => {
          if (blob.type && blob.type.indexOf('application/json') > -1) {
            throw new Error('document file could not be read');
          }
          return blob.arrayBuffer().then((buffer) =>
            pdfjsLib.getDocument({ data: buffer }).promise
          );
        })
        .then((pdf) => {
          const pages = [];
          for (let i = 1; i <= pdf.numPages; i++) pages.push({ ratio: 1.414 });
          this.pages = pages;
          this.pdf = pdf;
          this.docLoading = false;
          this.$nextTick(() => this.renderPages(pdf));
        })
        .catch((err) => {
          this.errorMessage = this.$t('editor.cannotShow') + ' (' + err.message + ')';
          this.docLoading = false;
        });
    },

    renderPages(pdf) {
      for (let i = 1; i <= pdf.numPages; i++) {
        this.renderPage(pdf, i);
      }
    },

    renderPage(pdf, pageNumber) {
      pdf.getPage(pageNumber).then((page) => {
        const index = pageNumber - 1;
        const holder = this.$refs['page' + index];
        const canvasRef = this.$refs['canvas' + index];
        const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef;
        const wrap = Array.isArray(holder) ? holder[0] : holder;
        if (!canvas || !wrap) return;

        const unscaled = page.getViewport({ scale: 1 });
        // Sayfa oranını gerçek belgeden al, kutu ona göre yükselsin
        this.$set(this.pages, index, { ratio: unscaled.height / unscaled.width });

        this.$nextTick(() => {
          const width = wrap.clientWidth;
          const viewport = page.getViewport({ scale: width / unscaled.width });
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          page.render({ canvasContext: canvas.getContext('2d'), viewport: viewport });
        });
      });
    },

    // --- Alan yerleştirme ----------------------------------------------

    fieldsOnPage(pageNumber) {
      return this.fields.filter((f) => (f.page || 1) === pageNumber);
    },

    placeField(event, index) {
      if (!this.roles.length) {
        this.errorMessage = this.$t('editor.errFirstRole');
        return;
      }
      const wrap = this.wrapFor(index);
      if (!wrap) return;

      const rect = wrap.getBoundingClientRect();
      const size = DEFAULT_SIZE[this.newType] || DEFAULT_SIZE.default;

      // Tıklanan nokta kutunun sol üstü değil ortası olsun
      let x = (event.clientX - rect.left) / rect.width - size.w / 2;
      let y = (event.clientY - rect.top) / rect.height - size.h / 2;
      x = Math.min(Math.max(x, 0), 1 - size.w);
      y = Math.min(Math.max(y, 0), 1 - size.h);

      const role = this.roles.find((r) => r.key === this.newRole) || this.roles[0];

      const key = this.uniqueKey(this.newType, role.key);
      this.selectedKey = key;
      this.fields.push({
        key: key,
        label: this.defaultLabel(this.newType, role.name),
        type: this.newType,
        roleKey: role.key,
        required: true,
        page: index + 1,
        x: x, y: y, w: size.w, h: size.h,
        fontSize: 11
      });
    },

    // Sıra numarasını fields.length'ten türetmek çakışma üretiyordu:
    // ortadaki alan silinince sonraki alan var olan bir anahtarı alıyordu
    uniqueKey(type, roleKey) {
      const taken = {};
      this.fields.forEach((f) => { taken[f.key] = true; });
      const base = type + '_' + roleKey + '_';
      let n = 1;
      while (taken[base + n]) n++;
      return base + n;
    },

    removeField(i) {
      const removed = this.fields[i];
      if (removed && removed.key === this.selectedKey) this.selectedKey = '';
      this.fields.splice(i, 1);
    },

    removeByKey(key) {
      const index = this.fields.findIndex((f) => f.key === key);
      if (index > -1) this.removeField(index);
    },

    selectField(field) {
      this.selectedKey = field.key;
    },

    isSignature(field) {
      return field.type === 'signature' || field.type === 'initials';
    },

    typeIcon(type) {
      const found = this.fieldTypes.find((t) => t.value === type);
      return found ? found.icon : 'fa-square-o';
    },

    // Yakınlaştırma sayfa genişliğini değiştiriyor; koordinatlar oransal
    // tutulduğu için alanlar olduğu yerde kalıyor
    setZoom(value) {
      this.zoom = Math.min(Math.max(Math.round(value * 10) / 10, 0.6), 1.6);
      // Genişlik değişince PDF'i yeniden çizmek gerekiyor, yoksa bulanık
      if (this.source === 'upload' && this.pdf) {
        this.$nextTick(() => this.renderPages(this.pdf));
      }
    },

    wrapFor(index) {
      const holder = this.$refs['page' + index];
      return Array.isArray(holder) ? holder[0] : holder;
    },

    boxStyle(f) {
      return {
        left: (f.x * 100) + '%',
        top: (f.y * 100) + '%',
        width: (f.w * 100) + '%',
        height: (f.h * 100) + '%'
      };
    },

    // --- Sürükleme ve boyutlandırma ------------------------------------

    startDrag(event, field, index) {
      const wrap = this.wrapFor(index);
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      this.drag = {
        mode: 'move',
        field: field,
        rect: rect,
        startX: event.clientX,
        startY: event.clientY,
        originX: field.x,
        originY: field.y
      };
    },

    startResize(event, field, index) {
      const wrap = this.wrapFor(index);
      if (!wrap) return;
      const rect = wrap.getBoundingClientRect();
      this.drag = {
        mode: 'resize',
        field: field,
        rect: rect,
        startX: event.clientX,
        startY: event.clientY,
        originW: field.w,
        originH: field.h
      };
    },

    onDrag(event) {
      if (!this.drag) return;
      const d = this.drag;
      const dx = (event.clientX - d.startX) / d.rect.width;
      const dy = (event.clientY - d.startY) / d.rect.height;

      if (d.mode === 'move') {
        d.field.x = Math.min(Math.max(d.originX + dx, 0), 1 - d.field.w);
        d.field.y = Math.min(Math.max(d.originY + dy, 0), 1 - d.field.h);
      } else {
        // Çok küçülürse tıklanamaz hale geliyor, alt sınır koyuyoruz
        d.field.w = Math.min(Math.max(d.originW + dx, 0.03), 1 - d.field.x);
        d.field.h = Math.min(Math.max(d.originH + dy, 0.015), 1 - d.field.y);
      }
    },

    endDrag() {
      this.drag = null;
    },

    // --- Kaydet ---------------------------------------------------------

    // activate: true = aktifleştir, 'draft' = taslağa al, false = durumu değiştirme
    save(activate) {
      if (this.saving) return;

      // Etiketi boş alanı yalnız söylemiyoruz, seçip gösteriyoruz
      const unlabeled = this.fields.find((f) => !String(f.label || '').trim());
      if (unlabeled) {
        this.errorMessage = this.$t('editor.errLabel');
        this.selectedKey = unlabeled.key;
        return;
      }
      if (activate === true && !this.hasSignature) {
        this.errorMessage = this.$t('editor.needSignature');
        return;
      }

      let status = this.template.status;
      if (activate === true) status = 'active';
      else if (activate === 'draft') status = 'draft';

      this.saving = true;
      this.errorMessage = '';

      api.put('/templates/' + this.currentId, {
        fields: this.fields,
        roles: this.roles,
        status: status
      })
        .then((body) => {
          this.template = body.template;
          this.currentId = body.template._id;      // versiyon değiştiyse takip et
          this.fields = (body.template.fields || []).map((f) => Object.assign({}, f));
          this.$emit('saved', body.message);
          if (activate === true) this.$emit('close');
        })
        .catch((err) => { this.errorMessage = err.message; })
        .then(() => { this.saving = false; });
    },

    // --- Etiketler ------------------------------------------------------

    defaultLabel(type, roleName) {
      return roleName + ' — ' + this.typeLabel(type);
    },
    typeLabel(type) {
      const map = { signature: 'tSignature', initials: 'tInitials', text: 'tText',
        date: 'tDate', number: 'tNumber', email: 'tEmail', checkbox: 'tCheckbox',
        dropdown: 'tDropdown', textarea: 'tTextarea', stamp: 'tStamp' };
      return map[type] ? this.$t('editor.' + map[type]) : type;
    },
    typeClass(type) {
      return (type === 'signature' || type === 'initials') ? 'is-link' : 'is-light';
    },
    roleName(key) {
      const role = this.roles.find((r) => r.key === key);
      return role ? role.name : key;
    }
  }
};
</script>
<style scoped>
/* Tam ekran: alan yerleştirme dar bir modalda yapılacak iş değil */
.editor {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: flex;
  flex-direction: column;
  background: #eceef4;
}

/* --- Üst çubuk --- */
.ed-top {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 18px;
  height: 58px;
  padding: 0 16px 0 20px;
  background: #1c1c33;
  color: #e7e9f2;
}

.ed-title { display: flex; align-items: center; gap: 10px; min-width: 0; }

.ed-title h2 {
  font-size: 15.5px;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* --- Yakınlaştırma --- */
.ed-zoom {
  display: flex;
  align-items: center;
  gap: 3px;
  margin: 0 auto;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 9px;
  padding: 3px;
}

.zoom-btn {
  min-width: 28px;
  height: 26px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  color: #c3c8de;
  font-size: 15px;
  cursor: pointer;
}

.zoom-btn:hover:not(:disabled) { background: rgba(255, 255, 255, 0.12); color: #fff; }
.zoom-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.zoom-btn.is-text { font-size: 12px; font-weight: 600; padding: 0 9px; }

.zoom-value {
  font-size: 12px;
  font-weight: 600;
  color: #c3c8de;
  min-width: 42px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.ed-actions { display: flex; align-items: center; gap: 8px; flex: 0 0 auto; }

.close-btn {
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: #aeb4cc;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
}

.close-btn:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }

/* --- Uyarı --- */
/* Üst çubuğun hemen altında, tam genişlikte: modal içindeyken
   kaydırınca gözden kayboluyordu */
.toast {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 12px 18px;
  font-size: 13.5px;
  background: #fdeceb;
  color: #a8322c;
  border-bottom: 1px solid #f4cfcc;
}

.toast-icon { font-size: 15px; }

.toast-close {
  margin-left: auto;
  background: transparent;
  border: 0;
  font-size: 20px;
  line-height: 1;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
}

/* --- Gövde --- */
.ed-body { flex: 1 1 auto; display: flex; min-height: 0; }

/* --- Sol panel --- */
.ed-side {
  flex: 0 0 300px;
  background: #fff;
  border-right: 1px solid #e0e4ee;
  overflow-y: auto;
  padding: 14px;
}

.ready {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  border-radius: 10px;
  padding: 12px 13px;
  font-size: 12.5px;
  line-height: 1.5;
  margin-bottom: 14px;
}

.ready.is-ok { background: #e4f6ef; color: #0f6e5c; }
.ready.is-warn { background: #fdf2e2; color: #8a6116; }
.ready-icon { font-size: 14px; margin-top: 1px; }

.panel {
  border-top: 1px solid #eef0f6;
  padding-top: 14px;
  margin-top: 14px;
}

.panel:first-of-type { border-top: 0; padding-top: 0; margin-top: 0; }

.panel h3 {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #9aa1b5;
  margin-bottom: 8px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.panel-head h3 { margin-bottom: 0; }

.count {
  background: #f2f4fa;
  color: #5a6180;
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11.5px;
  font-weight: 700;
}

.hint { font-size: 12px; color: #98a0b5; line-height: 1.55; margin-bottom: 11px; }
.hint.mt { margin-top: 12px; margin-bottom: 0; }

.err { font-size: 12px; color: #b63934; margin-top: 6px; line-height: 1.5; }

/* --- Tür ızgarası --- */
.types {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
  margin-bottom: 14px;
}

.type {
  display: flex;
  align-items: center;
  gap: 7px;
  background: #fff;
  border: 1px solid #e2e6f0;
  border-radius: 9px;
  padding: 9px 10px;
  font-size: 12.5px;
  font-weight: 600;
  color: #5a6180;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s, background-color 0.15s, color 0.15s;
}

.type:hover { border-color: #c3cbdd; }

.type.is-active {
  border-color: #00b89c;
  background: #f0fbf9;
  color: #0b6e5f;
}

.type i { font-size: 12px; opacity: 0.85; width: 14px; text-align: center; }

.side-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #5a6180;
  margin-bottom: 5px;
}

.select-wrap { position: relative; display: block; }

.select-input {
  width: 100%;
  height: 36px;
  border: 1px solid #dde1ea;
  border-radius: 8px;
  padding: 0 30px 0 11px;
  font-size: 13px;
  color: #2b3146;
  background: #fff;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  cursor: pointer;
}

.select-input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.16);
}

.select-input:disabled { background: #f6f7fa; color: #a7aec1; cursor: not-allowed; }

.select-wrap::after {
  content: "";
  position: absolute;
  right: 12px;
  top: 50%;
  margin-top: -2px;
  width: 0;
  height: 0;
  border-left: 4.5px solid transparent;
  border-right: 4.5px solid transparent;
  border-top: 5px solid #8b93a8;
  pointer-events: none;
}

/* --- Alan listesi --- */
.field-list { display: flex; flex-direction: column; gap: 5px; }

.field-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border: 1px solid #eef0f6;
  border-radius: 9px;
  padding: 9px 9px 9px 10px;
  cursor: pointer;
  transition: border-color 0.15s, background-color 0.15s;
}

.field-item:hover { border-color: #d3d9e8; }

/* Listedeki seçili alan ile belgedeki kutu aynı anda vurgulanıyor:
   hangi satırın hangi kutu olduğunu aramak zorunda kalmıyorsun */
.field-item.is-selected {
  border-color: #00b89c;
  background: #f4fdfb;
}

.dot {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 11px;
}

.dot.is-sign { background: #00b89c; }
.dot.is-data { background: #7d86b4; }

.field-item-main { flex: 1 1 auto; min-width: 0; }

.mini-input {
  width: 100%;
  height: 30px;
  border: 1px solid #e2e6f0;
  border-radius: 7px;
  padding: 0 9px;
  font-size: 12.5px;
  color: #2b3146;
  background: #fff;
}

.mini-input:focus {
  outline: 0;
  border-color: #00b89c;
  box-shadow: 0 0 0 2px rgba(0, 209, 178, 0.16);
}

.field-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  font-size: 11px;
  color: #98a0b5;
  margin-top: 5px;
}

.mini-check {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #79809a;
  margin-top: 6px;
  cursor: pointer;
}

.icon-btn {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 7px;
  border: 1px solid #e2e6f0;
  background: #fff;
  color: #a7aec1;
  font-size: 11px;
  cursor: pointer;
}

.icon-btn.is-danger:hover { border-color: #e0a9a5; color: #b63934; background: #fdf3f2; }

/* --- Belge alanı --- */
.ed-main {
  flex: 1 1 auto;
  min-width: 0;
  overflow-y: auto;
  padding: 24px 20px 60px;
}

.stage { margin: 0 auto; transition: max-width 0.15s ease; }

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #fff;
  border-radius: 12px;
  padding: 50px;
  color: #98a0b5;
  font-size: 14px;
}

.page-block { margin-bottom: 22px; }

.page-no {
  font-size: 11.5px;
  font-weight: 600;
  color: #8b93a8;
  margin-bottom: 6px;
}

.page-wrap {
  position: relative;
  width: 100%;
  height: 0;
  background: #fff;
  border-radius: 3px;
  box-shadow: 0 3px 14px rgba(24, 24, 48, 0.13);
  cursor: crosshair;
  overflow: hidden;
}

.page-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.page-html { padding: 7%; overflow: hidden; font-size: 11px; }

.empty-doc {
  background: #fff;
  border-radius: 12px;
  padding: 44px 20px;
  text-align: center;
  color: #98a0b5;
  font-size: 13.5px;
}

/* --- Alan kutuları --- */
.field-box {
  position: absolute;
  border: 1.5px solid #7d86b4;
  background: rgba(125, 134, 180, 0.14);
  border-radius: 3px;
  cursor: move;
  transition: box-shadow 0.12s;
}

.field-box:hover { box-shadow: 0 0 0 3px rgba(125, 134, 180, 0.18); }

.field-box.is-signature {
  border-color: #00a88f;
  border-style: dashed;
  background: rgba(0, 209, 178, 0.13);
}

.field-box.is-selected {
  border-color: #00a88f;
  border-style: solid;
  box-shadow: 0 0 0 3px rgba(0, 209, 178, 0.3);
  z-index: 3;
}

.field-tag {
  position: absolute;
  top: -1px;
  left: 3px;
  right: 16px;
  font-size: 9.5px;
  line-height: 13px;
  color: #3b4480;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}

.field-box.is-signature .field-tag { color: #0b6e5f; }
.field-tag i { margin-right: 3px; opacity: 0.8; }

/* Silme düğmesi kutunun içinde: listeye gidip aramaya gerek yok */
.box-del {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: 1px solid #e0a9a5;
  background: #fff;
  color: #b63934;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.12s;
}

.field-box:hover .box-del,
.field-box.is-selected .box-del { opacity: 1; }

.resize-handle {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 11px;
  height: 11px;
  background: #fff;
  border: 1.5px solid #00a88f;
  border-radius: 3px;
  cursor: nwse-resize;
}

/* --- Rozet ve düğmeler --- */
.pill {
  border-radius: 999px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.pill.is-ok { background: rgba(0, 209, 178, 0.2); color: #7cf2dc; }
.pill.is-warn { background: rgba(255, 196, 87, 0.2); color: #ffd48a; }
.pill.is-idle { background: rgba(255, 255, 255, 0.12); color: #c3c8de; }

.btn-primary,
.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  border-radius: 8px;
  padding: 9px 16px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.btn-primary {
  background: linear-gradient(135deg, #00d1b2 0%, #00a88f 100%);
  border: 1px solid transparent;
  color: #06302a;
}

.btn-primary:hover:not(:disabled) { background: linear-gradient(135deg, #0ddcbe 0%, #00b79c 100%); }

.btn-primary:disabled {
  background: rgba(255, 255, 255, 0.1);
  color: #7c839c;
  cursor: not-allowed;
}

.btn-ghost {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.24);
  color: #e7e9f2;
}

.btn-ghost:hover { background: rgba(255, 255, 255, 0.1); border-color: rgba(255, 255, 255, 0.4); }

.btn-primary.is-loading,
.btn-ghost.is-loading { opacity: 0.7; pointer-events: none; }

@media screen and (max-width: 900px) {
  .ed-body { flex-direction: column; }
  .ed-side { flex: 0 0 auto; max-height: 42vh; border-right: 0; border-bottom: 1px solid #e0e4ee; }
  .ed-zoom { display: none; }
  .ed-top { gap: 10px; padding: 0 10px 0 14px; }
  .btn-ghost, .btn-primary { padding: 8px 12px; font-size: 12.5px; }
}
</style>


// WEBPACK FOOTER //
// src/Components/TemplateEditor.vue